import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import axios, { CancelTokenSource } from "axios";
import { v4 as uuidv4, validate as validateUuid } from "uuid";
import { getCurrentMidwaySubject } from "../auth/MidwayJwtToken";
import { ChatUatApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";
import {
  ChatUatAction as GeneratedChatUatAction,
  ChatUatActionOutcomeEnum,
  ChatUatActionResponseResponseTypeEnum,
  ChatUatAudience,
  ChatUatContextReference,
  ChatUatFeedbackRequestRatingEnum,
  ChatUatFeedbackRequestReasonEnum,
  ChatUatFailureCode,
  ChatUatJobCompletedResponseStatusEnum,
  ChatUatJobFailedResponse as GeneratedChatUatJobFailedResponse,
  ChatUatJobFailedResponseStatusEnum,
  ChatUatJobPendingResponse as GeneratedChatUatJobPendingResponse,
  ChatUatJobPendingResponseStatusEnum,
  ChatUatJobStateLostResponse as GeneratedChatUatJobStateLostResponse,
  ChatUatJobStateLostResponseStatusEnum,
  ChatUatJobStatusResponse as GeneratedChatUatJobStatusResponse,
  ChatUatMessageResponseResponseTypeEnum,
  ChatUatResponse as GeneratedChatUatResponse,
  ChatUatReviewResponseGroundedInEnum,
  ChatUatReviewResponseResponseTypeEnum,
} from "../lusca-search-api/generated-src";

export interface ChatUatEntry {
  id: string;
  role: "user" | "assistant";
  content: string;
  groundedIn?: string[];
  action?: ChatUatAction;
  isPassingReview?: boolean;
  isError?: boolean;
  failure?: ChatUatFailure;
  feedbackTarget?: ChatUatFeedbackTarget;
  feedbackRating?: ChatUatFeedbackRequestRatingEnum;
}

type ChatUatResponseType = GeneratedChatUatResponse["responseType"];
export type ChatUatAction = GeneratedChatUatAction;

export interface ChatUatArtefact {
  raw: string;
  primary: string;
  details: string;
}

export interface ChatUatFailure {
  jobId: string;
  occurredAt: number;
  failureCode?: ChatUatFailureCode;
}

interface ChatUatTurn {
  jobId: string;
  prompt: string;
  conversationId: string;
  generation: number;
  cancelSource: CancelTokenSource;
  resumeOnly: boolean;
  preserveArtefact: boolean;
  deadline: number;
  context?: ChatUatContextReference;
  audience?: ChatUatAudience;
}

interface StoredChatUatTurn {
  actorId: string;
  jobId: string;
  prompt: string;
  conversationId: string;
  deadline: number;
  artefact?: string;
  context?: ChatUatContextReference;
  audience?: ChatUatAudience;
}

interface ChatUatFeedbackTarget {
  jobId: string;
  conversationId: string;
  response: ValidChatUatResponse;
  context?: ChatUatContextReference;
}

type ValidChatUatJobStatus = GeneratedChatUatJobStatusResponse;

interface ValidChatUatResponse {
  conversationId: string;
  responseType: ChatUatResponseType;
  conversational: string;
  artefact?: string;
  groundedIn?: string[];
  action?: ChatUatAction;
}

interface SendMessageOptions {
  preserveArtefact?: boolean;
  onSubmitted?: () => void;
}

export interface ChatUatFeedbackInput {
  entryId: string;
  rating: ChatUatFeedbackRequestRatingEnum;
  reason?: ChatUatFeedbackRequestReasonEnum;
  comment?: string;
}

const REQUEST_RESET_ERROR =
  "This request could not be confirmed, so the conversation was reset. You can try again.";
const REQUEST_REJECTED_ERROR =
  "This request was not accepted. Your previous conversation is unchanged; you can try again.";
const REVIEW_TOO_LARGE_ERROR =
  "This order contains too much shadow-test evidence to generate a complete review in one response. Start a new conversation and request a concise summary or ask about a specific difference.";
const INTERNAL_ERROR =
  "UAT Insights could not complete this request. If it happens again, share the support reference with TRACK.";
const OUTCOME_UNCERTAIN_ERROR =
  "This request was accepted, but its outcome could not be confirmed. You can continue this conversation. Check any requested action before retrying it.";
const OUTCOME_UNCERTAIN_ERROR_NAME = "ChatUatOutcomeUncertainError";
const STATE_LOST_ERROR_NAME = "ChatUatStateLostError";
const RECOVERY_STORAGE_ERROR =
  "This request was not submitted because UAT Insights could not save recovery state in this browser. Check session storage and try again.";
const JOB_TIMEOUT_MS = 5 * 60 * 1000;
const MIN_POLL_DELAY_MS = 250;
const MAX_POLL_DELAY_MS = 10000;
const MAX_TRANSIENT_POLL_RETRIES = 5;
const MAX_STORED_PROMPT_CHARACTERS = 4000;
const MAX_TERMINAL_ERROR_CHARACTERS = 256;
const ACTIVE_JOB_STORAGE_KEY = "lusca-chat-uat-active-job";
const COVERAGE_LINE = /^(Evidence present:|Grounded in:)/;
const ZERO_ISSUE_CONVERSATIONAL = "0 issue(s) found.";
const ZERO_ISSUE_VERDICT = "Verdict: 0 issues — no differences found.";
const GROUNDING_SOURCES = new Set<string>(
  Object.values(ChatUatReviewResponseGroundedInEnum)
);
const RESPONSE_TYPES = new Set<string>([
  ...Object.values(ChatUatReviewResponseResponseTypeEnum),
  ...Object.values(ChatUatMessageResponseResponseTypeEnum),
  ...Object.values(ChatUatActionResponseResponseTypeEnum),
]);
const REVIEW_RESPONSE_KEYS = new Set([
  "conversationId",
  "responseType",
  "conversational",
  "artefact",
  "groundedIn",
]);
const MESSAGE_RESPONSE_KEYS = new Set([
  "conversationId",
  "responseType",
  "conversational",
]);
const ACTION_RESPONSE_KEYS = new Set([
  "conversationId",
  "responseType",
  "conversational",
  "action",
]);
const ACTION_OUTCOMES = new Set<string>(
  Object.values(ChatUatActionOutcomeEnum)
);
const CHAT_UAT_AUDIENCES = new Set<ChatUatAudience>(["customer", "engineer"]);
const ACTION_TYPE = /^[A-Za-z][A-Za-z0-9._-]*$/;
const JOB_PENDING_STATUSES = new Set<string>(
  Object.values(ChatUatJobPendingResponseStatusEnum)
);
const JOB_PENDING_KEYS = new Set([
  "jobId",
  "conversationId",
  "status",
  "retryAfterMs",
]);
const JOB_COMPLETED_KEYS = new Set([
  "jobId",
  "conversationId",
  "status",
  "response",
]);
const JOB_FAILED_KEYS = new Set(["jobId", "conversationId", "status", "error"]);
const JOB_FAILED_WITH_CODE_KEYS = new Set([
  "jobId",
  "conversationId",
  "status",
  "error",
  "failureCode",
]);
const JOB_FAILURE_CODES = new Set<string>([
  "review_too_large",
  "timeout",
  "execution_limit",
  "cancelled",
  "lost",
  "internal_error",
]);
const JOB_TERMINAL_ERROR_STATUSES = new Set<string>([
  ChatUatJobFailedResponseStatusEnum.Failed,
  ChatUatJobStateLostResponseStatusEnum.StateLost,
]);
const STORED_JOB_KEYS = new Set([
  "actorId",
  "jobId",
  "conversationId",
  "prompt",
  "deadline",
  "artefact",
  "context",
  "audience",
]);

class ChatUatFailedError extends Error {
  readonly jobId: string;

  readonly failureCode?: ChatUatFailureCode;

  readonly occurredAt: number;

  constructor(
    message: string,
    jobId: string,
    failureCode?: ChatUatFailureCode
  ) {
    super(message);
    this.name = "ChatUatFailedError";
    this.jobId = jobId;
    this.failureCode = failureCode;
    this.occurredAt = Date.now();
  }
}

function failureMessage(error: ChatUatFailedError): string {
  if (error.failureCode === "review_too_large") {
    return REVIEW_TOO_LARGE_ERROR;
  }
  if (error.failureCode === "internal_error") {
    return INTERNAL_ERROR;
  }
  return error.message;
}

function failureEntry(error: ChatUatFailedError): ChatUatEntry {
  return {
    id: nextEntryId(),
    role: "assistant",
    content: failureMessage(error),
    isError: true,
    failure: {
      jobId: error.jobId,
      occurredAt: error.occurredAt,
      ...(error.failureCode ? { failureCode: error.failureCode } : {}),
    },
  };
}

interface ChatUatStateLostError extends Error {
  name: typeof STATE_LOST_ERROR_NAME;
}

interface ChatUatOutcomeUncertainError extends Error {
  name: typeof OUTCOME_UNCERTAIN_ERROR_NAME;
}

function createStateLostError(message: string): ChatUatStateLostError {
  const error = new Error(message) as ChatUatStateLostError;
  error.name = STATE_LOST_ERROR_NAME;
  return error;
}

function isStateLostError(error: unknown): error is ChatUatStateLostError {
  return error instanceof Error && error.name === STATE_LOST_ERROR_NAME;
}

function createOutcomeUncertainError(): ChatUatOutcomeUncertainError {
  const error = new Error(
    OUTCOME_UNCERTAIN_ERROR
  ) as ChatUatOutcomeUncertainError;
  error.name = OUTCOME_UNCERTAIN_ERROR_NAME;
  return error;
}

function isOutcomeUncertainError(
  error: unknown
): error is ChatUatOutcomeUncertainError {
  return error instanceof Error && error.name === OUTCOME_UNCERTAIN_ERROR_NAME;
}

let entryCounter = 0;
function nextEntryId(): string {
  entryCounter += 1;
  return `chat-uat-entry-${entryCounter}`;
}

function isGroundingSources(value: unknown): value is string[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(
      (source) =>
        typeof source === "string" &&
        source.trim().length > 0 &&
        GROUNDING_SOURCES.has(source)
    ) &&
    new Set(value).size === value.length
  );
}

function hasOnlyAllowedKeys(
  value: object,
  allowedKeys: ReadonlySet<string>
): boolean {
  return Object.keys(value).every((key) => allowedKeys.has(key));
}

function isCanonicalUuid(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.toLowerCase() &&
    validateUuid(value)
  );
}

function isChatUatContextReference(
  value: unknown
): value is ChatUatContextReference {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const { runId, taskGid, orderId } = value as {
    runId?: unknown;
    taskGid?: unknown;
    orderId?: unknown;
  };
  return (
    hasOnlyAllowedKeys(value, new Set(["runId", "taskGid", "orderId"])) &&
    isCanonicalUuid(runId) &&
    typeof taskGid === "string" &&
    /^[0-9]{1,32}$/.test(taskGid) &&
    (orderId === undefined ||
      orderId === null ||
      (typeof orderId === "string" &&
        orderId.length > 0 &&
        orderId.length <= 128))
  );
}

function isChatUatAudience(value: unknown): value is ChatUatAudience {
  return (
    typeof value === "string" &&
    CHAT_UAT_AUDIENCES.has(value as ChatUatAudience)
  );
}

function readStoredChatUatTurn(): StoredChatUatTurn | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  try {
    const raw = window.sessionStorage.getItem(ACTIVE_JOB_STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
      throw new Error("Invalid stored ChatUAT job");
    }
    const {
      actorId,
      jobId,
      conversationId,
      prompt,
      deadline,
      artefact,
      context,
      audience,
    } = value as {
      actorId?: unknown;
      jobId?: unknown;
      conversationId?: unknown;
      prompt?: unknown;
      deadline?: unknown;
      artefact?: unknown;
      context?: unknown;
      audience?: unknown;
    };
    const currentActorId = getCurrentMidwaySubject();
    if (
      !currentActorId ||
      typeof actorId !== "string" ||
      actorId !== currentActorId
    ) {
      throw new Error("Stored ChatUAT job belongs to another actor");
    }
    if (
      artefact !== undefined &&
      (typeof artefact !== "string" || !artefact.trim())
    ) {
      throw new Error("Invalid stored ChatUAT artefact");
    }
    if (context !== undefined && !isChatUatContextReference(context)) {
      throw new Error("Invalid stored ChatUAT context");
    }
    if (audience !== undefined && !isChatUatAudience(audience)) {
      throw new Error("Invalid stored ChatUAT audience");
    }
    if (
      !hasOnlyAllowedKeys(value, STORED_JOB_KEYS) ||
      !isCanonicalUuid(jobId) ||
      !isCanonicalUuid(conversationId) ||
      typeof prompt !== "string" ||
      !prompt.trim() ||
      prompt !== prompt.trim() ||
      Array.from(prompt).length > MAX_STORED_PROMPT_CHARACTERS ||
      !Number.isSafeInteger(deadline) ||
      (deadline as number) <= 0
    ) {
      throw new Error("Invalid stored ChatUAT job");
    }
    const storedTurn: StoredChatUatTurn = {
      actorId,
      jobId,
      conversationId,
      prompt,
      deadline: deadline as number,
      ...(artefact === undefined ? {} : { artefact }),
      ...(context === undefined ? {} : { context }),
      ...(audience === undefined ? {} : { audience }),
    };
    return storedTurn;
  } catch (_error) {
    try {
      window.sessionStorage.removeItem(ACTIVE_JOB_STORAGE_KEY);
    } catch (_storageError) {
      // Storage can be unavailable under restrictive browser policies.
    }
    return undefined;
  }
}

function storeChatUatTurn(turn: Omit<StoredChatUatTurn, "actorId">): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const actorId = getCurrentMidwaySubject();
  if (!actorId) {
    return false;
  }
  const storedTurn: StoredChatUatTurn = { ...turn, actorId };
  const serializedTurn = JSON.stringify(storedTurn);
  try {
    window.sessionStorage.setItem(ACTIVE_JOB_STORAGE_KEY, serializedTurn);
    if (
      window.sessionStorage.getItem(ACTIVE_JOB_STORAGE_KEY) !== serializedTurn
    ) {
      throw new Error("ChatUAT recovery state was not persisted");
    }
    return true;
  } catch (_error) {
    try {
      window.sessionStorage.removeItem(ACTIVE_JOB_STORAGE_KEY);
    } catch (_storageError) {
      // Storage cleanup is best effort.
    }
    return false;
  }
}

function clearStoredChatUatTurn(
  turn?: Pick<StoredChatUatTurn, "jobId" | "conversationId">
): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    if (turn) {
      const stored = readStoredChatUatTurn();
      if (
        stored &&
        (stored.jobId !== turn.jobId ||
          stored.conversationId !== turn.conversationId)
      ) {
        return;
      }
    }
    window.sessionStorage.removeItem(ACTIVE_JOB_STORAGE_KEY);
  } catch (_error) {
    // Storage cleanup is best effort.
  }
}

function validateJobStatus(
  value: unknown,
  expectedJobId: string,
  expectedConversationId: string,
  responseSource: "start" | "poll"
): ValidChatUatJobStatus {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("Invalid ChatUAT job response");
  }

  const {
    jobId,
    conversationId,
    status,
    retryAfterMs,
    response,
    error,
    failureCode,
  } = value as {
    jobId?: unknown;
    conversationId?: unknown;
    status?: unknown;
    retryAfterMs?: unknown;
    response?: unknown;
    error?: unknown;
    failureCode?: unknown;
  };
  if (
    jobId !== expectedJobId ||
    !isCanonicalUuid(jobId) ||
    !isCanonicalUuid(conversationId) ||
    conversationId !== expectedConversationId ||
    typeof status !== "string"
  ) {
    throw new Error("Invalid ChatUAT job response");
  }

  if (JOB_PENDING_STATUSES.has(status)) {
    if (
      !hasOnlyAllowedKeys(value, JOB_PENDING_KEYS) ||
      !Number.isInteger(retryAfterMs) ||
      (retryAfterMs as number) < MIN_POLL_DELAY_MS ||
      (retryAfterMs as number) > 10000
    ) {
      throw new Error("Invalid ChatUAT pending job response");
    }
    return value as ValidChatUatJobStatus;
  }

  if (status === "completed") {
    if (
      !hasOnlyAllowedKeys(value, JOB_COMPLETED_KEYS) ||
      typeof response !== "object" ||
      response === null ||
      Array.isArray(response)
    ) {
      throw new Error("Invalid ChatUAT completed job response");
    }
    return value as ValidChatUatJobStatus;
  }

  if (
    responseSource === "start" &&
    status === ChatUatJobStateLostResponseStatusEnum.StateLost
  ) {
    throw new Error("Invalid ChatUAT start response");
  }

  if (!JOB_TERMINAL_ERROR_STATUSES.has(status)) {
    throw new Error("Invalid ChatUAT failed job response");
  }
  const validError =
    typeof error === "string" &&
    Boolean(error.trim()) &&
    Array.from(error).length <= MAX_TERMINAL_ERROR_CHARACTERS;
  if (status === ChatUatJobFailedResponseStatusEnum.Failed) {
    if (
      !hasOnlyAllowedKeys(
        value,
        failureCode === undefined ? JOB_FAILED_KEYS : JOB_FAILED_WITH_CODE_KEYS
      ) ||
      !validError ||
      (failureCode !== undefined &&
        (typeof failureCode !== "string" ||
          !JOB_FAILURE_CODES.has(failureCode)))
    ) {
      throw new Error("Invalid ChatUAT failed job response");
    }
    return value as ValidChatUatJobStatus;
  }
  if (
    !hasOnlyAllowedKeys(value, JOB_FAILED_KEYS) ||
    failureCode !== undefined ||
    !validError
  ) {
    throw new Error("Invalid ChatUAT failed job response");
  }
  return value as ValidChatUatJobStatus;
}

function isPendingJobStatus(
  value: ValidChatUatJobStatus
): value is GeneratedChatUatJobPendingResponse {
  return JOB_PENDING_STATUSES.has(value.status);
}

function isTerminalJobStatus(
  value: ValidChatUatJobStatus
): value is
  | GeneratedChatUatJobFailedResponse
  | GeneratedChatUatJobStateLostResponse {
  return JOB_TERMINAL_ERROR_STATUSES.has(value.status);
}

function nextPollDelay(serverDelayMs: number, attempt: number): number {
  const backedOff = Math.min(
    serverDelayMs * 1.4 ** Math.min(attempt, 12),
    MAX_POLL_DELAY_MS
  );
  const jitter = 1 + Math.random() * 0.15;
  return Math.max(
    MIN_POLL_DELAY_MS,
    Math.min(MAX_POLL_DELAY_MS, Math.round(backedOff * jitter))
  );
}

function waitForPoll(
  delayMs: number,
  cancelSource: CancelTokenSource
): Promise<void> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      settled = true;
      resolve();
    }, delayMs);
    cancelSource.token.promise
      .then((reason) => {
        if (!settled) {
          settled = true;
          clearTimeout(timer);
          reject(reason);
        }
      })
      .catch((error) => reject(error));
  });
}

async function waitWithinDeadline(
  delayMs: number,
  cancelSource: CancelTokenSource,
  deadline: number
): Promise<void> {
  const remainingMs = deadline - Date.now();
  if (remainingMs <= 0) {
    throw new Error("ChatUAT job timed out");
  }
  await waitForPoll(Math.min(delayMs, remainingMs), cancelSource);
  if (Date.now() >= deadline) {
    throw new Error("ChatUAT job timed out");
  }
}

function isTransportOrServerError(error: unknown): boolean {
  if (axios.isCancel(error)) {
    return false;
  }
  return (
    axios.isAxiosError(error) &&
    (error.response === undefined || error.response.status >= 500)
  );
}

function isPreAcceptanceClientError(error: unknown): boolean {
  return (
    axios.isAxiosError(error) &&
    error.response !== undefined &&
    error.response.status >= 400 &&
    error.response.status < 500
  );
}

function isTransientPollError(error: unknown): boolean {
  return (
    isTransportOrServerError(error) ||
    (axios.isAxiosError(error) && error.response?.status === 429)
  );
}

function isPollHttpError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response !== undefined;
}

function shouldPreserveAcceptedJobAfterDeadline(
  error: unknown,
  deadline: number
): boolean {
  return (
    Date.now() >= deadline &&
    !isOutcomeUncertainError(error) &&
    !(error instanceof ChatUatFailedError) &&
    !isStateLostError(error)
  );
}

function validateAcceptedPayload<T>(validator: () => T): T {
  try {
    return validator();
  } catch (_error) {
    throw createOutcomeUncertainError();
  }
}

function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map((item) => canonicalJson(item)).join(",")}]`;
  }
  const object = value as Record<string, unknown>;
  return `{${Object.keys(object)
    .sort()
    .map((key) => `${JSON.stringify(key)}:${canonicalJson(object[key])}`)
    .join(",")}}`;
}

async function responseFingerprint(response: ValidChatUatResponse) {
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(canonicalJson(response))
  );
  return Array.from(new Uint8Array(digest))
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("");
}

function validateAction(value: unknown): ChatUatAction {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("Invalid ChatUAT action");
  }

  const { type, status, outcome, summary, reference } = value as {
    type?: unknown;
    status?: unknown;
    outcome?: unknown;
    summary?: unknown;
    reference?: unknown;
  };
  if (
    typeof type !== "string" ||
    !ACTION_TYPE.test(type) ||
    typeof status !== "string" ||
    !status.trim() ||
    typeof outcome !== "string" ||
    !ACTION_OUTCOMES.has(outcome) ||
    typeof summary !== "string" ||
    !summary.trim() ||
    Object.keys(value).some(
      (key) =>
        !["type", "status", "outcome", "summary", "reference"].includes(key)
    )
  ) {
    throw new Error("Invalid ChatUAT action");
  }

  let validatedReference: NonNullable<ChatUatAction["reference"]> | undefined;
  if (reference !== undefined) {
    if (
      typeof reference !== "object" ||
      reference === null ||
      Array.isArray(reference)
    ) {
      throw new Error("Invalid ChatUAT action");
    }
    const { label, value: referenceValue } = reference as {
      label?: unknown;
      value?: unknown;
    };
    if (
      typeof label !== "string" ||
      !label.trim() ||
      typeof referenceValue !== "string" ||
      !referenceValue.trim() ||
      Object.keys(reference).some((key) => !["label", "value"].includes(key))
    ) {
      throw new Error("Invalid ChatUAT action");
    }
    validatedReference = { label, value: referenceValue };
  }

  const validatedAction: ChatUatAction = {
    type,
    status,
    outcome: outcome as ChatUatActionOutcomeEnum,
    summary,
  };
  if (validatedReference) {
    validatedAction.reference = validatedReference;
  }
  return validatedAction;
}

function validateResponse(
  value: unknown,
  expectedConversationId?: string
): ValidChatUatResponse {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("Invalid ChatUAT response");
  }

  const {
    conversationId,
    responseType,
    conversational,
    artefact,
    groundedIn,
    action,
  } = value as {
    conversationId?: unknown;
    responseType?: unknown;
    conversational?: unknown;
    artefact?: unknown;
    groundedIn?: unknown;
    action?: unknown;
  };
  if (
    typeof conversationId !== "string" ||
    conversationId !== conversationId.toLowerCase() ||
    !validateUuid(conversationId) ||
    (expectedConversationId !== undefined &&
      conversationId !== expectedConversationId) ||
    typeof responseType !== "string" ||
    !RESPONSE_TYPES.has(responseType) ||
    typeof conversational !== "string" ||
    !conversational.trim()
  ) {
    throw new Error("Invalid ChatUAT response");
  }

  if (responseType === ChatUatReviewResponseResponseTypeEnum.Review) {
    if (
      !hasOnlyAllowedKeys(value, REVIEW_RESPONSE_KEYS) ||
      typeof artefact !== "string" ||
      !artefact.trim() ||
      !isGroundingSources(groundedIn) ||
      action !== undefined
    ) {
      throw new Error("Invalid ChatUAT review response");
    }
    return {
      conversationId,
      responseType,
      conversational,
      artefact,
      groundedIn,
    };
  }

  if (artefact !== undefined || groundedIn !== undefined) {
    throw new Error("Invalid ChatUAT non-review response");
  }
  if (responseType === ChatUatMessageResponseResponseTypeEnum.Message) {
    if (
      !hasOnlyAllowedKeys(value, MESSAGE_RESPONSE_KEYS) ||
      action !== undefined
    ) {
      throw new Error("Invalid ChatUAT message response");
    }
    return {
      conversationId,
      responseType,
      conversational,
    };
  }

  if (!hasOnlyAllowedKeys(value, ACTION_RESPONSE_KEYS)) {
    throw new Error("Invalid ChatUAT action response");
  }
  return {
    conversationId,
    responseType: ChatUatActionResponseResponseTypeEnum.Action,
    conversational,
    action: validateAction(action),
  };
}

function isPassingReview(response: ValidChatUatResponse): boolean {
  return (
    response.responseType === ChatUatReviewResponseResponseTypeEnum.Review &&
    response.conversational === ZERO_ISSUE_CONVERSATIONAL &&
    response.artefact
      ?.split("\n")
      .some((line) => line.trim() === ZERO_ISSUE_VERDICT) === true
  );
}

export function parseArtefact(raw: string): ChatUatArtefact {
  const body = raw.trim();
  const primary: string[] = [];
  const details: string[] = [];
  body.split("\n").forEach((line) => {
    (COVERAGE_LINE.test(line.trim()) ? details : primary).push(line);
  });
  return {
    raw: body,
    primary: primary
      .join("\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim(),
    details: details.join("\n").trim(),
  };
}

export default function useChatUat(
  currentContext?: ChatUatContextReference,
  audience?: ChatUatAudience
) {
  const [restoredTurn] = useState(readStoredChatUatTurn);
  const [entries, setEntries] = useState<ChatUatEntry[]>(() =>
    restoredTurn
      ? [
          {
            id: nextEntryId(),
            role: "user",
            content: restoredTurn.prompt,
          },
        ]
      : []
  );
  const [rawArtefact, setRawArtefact] = useState<string | null>(
    restoredTurn?.artefact ?? null
  );
  const [isResuming, setIsResuming] = useState(restoredTurn !== undefined);
  const [isOutcomeUncertain, setIsOutcomeUncertain] = useState(false);
  const conversationId = useRef<string | undefined>(
    restoredTurn?.conversationId
  );
  const activeRequest = useRef<CancelTokenSource | null>(null);
  const generation = useRef(0);
  const submitting = useRef(restoredTurn !== undefined);
  const preservedArtefactFlow = useRef(restoredTurn?.artefact !== undefined);
  const resumeStarted = useRef(false);

  useEffect(
    () => () => {
      generation.current += 1;
      submitting.current = false;
      preservedArtefactFlow.current = false;
      activeRequest.current?.cancel("ChatUAT request abandoned");
      activeRequest.current = null;
    },
    []
  );

  const {
    isLoading: mutationLoading,
    mutate,
    reset: resetMutation,
  } = useMutation<ValidChatUatResponse, Error, ChatUatTurn>({
    retry: false,
    mutationFn: async ({
      jobId,
      prompt,
      conversationId: turnConversationId,
      cancelSource,
      resumeOnly,
      deadline,
      context,
      audience: turnAudience,
    }: ChatUatTurn): Promise<ValidChatUatResponse> => {
      const api = ChatUatApiFactory();
      const deadlineTimer = setTimeout(() => {
        cancelSource.cancel("ChatUAT job timed out");
      }, Math.max(0, deadline - Date.now()));

      const pollWithRetry = async <T>(
        request: () => Promise<T>,
        retryAttempt = 0
      ): Promise<T> => {
        if (Date.now() >= deadline) {
          throw new Error("ChatUAT job timed out");
        }
        try {
          return await request();
        } catch (error) {
          if (!isTransientPollError(error)) {
            throw error;
          }
          if (retryAttempt >= MAX_TRANSIENT_POLL_RETRIES) {
            throw createOutcomeUncertainError();
          }
          await waitWithinDeadline(
            nextPollDelay(MIN_POLL_DELAY_MS, retryAttempt),
            cancelSource,
            deadline
          );
          return pollWithRetry(request, retryAttempt + 1);
        }
      };

      const pollJob = async (
        previousStatus?: GeneratedChatUatJobPendingResponse,
        pollAttempt = 0
      ): Promise<ValidChatUatResponse> => {
        try {
          if (previousStatus) {
            await waitWithinDeadline(
              nextPollDelay(previousStatus.retryAfterMs, pollAttempt),
              cancelSource,
              deadline
            );
          }
          const pollResult = await pollWithRetry(() =>
            api.getChatUatJob(turnConversationId, jobId, {
              cancelToken: cancelSource.token,
            })
          );
          const nextStatus = validateAcceptedPayload(() =>
            validateJobStatus(
              pollResult.data,
              jobId,
              turnConversationId,
              "poll"
            )
          );
          if (isPendingJobStatus(nextStatus)) {
            return await pollJob(nextStatus, pollAttempt + 1);
          }
          if (isTerminalJobStatus(nextStatus)) {
            if (
              nextStatus.status ===
              ChatUatJobStateLostResponseStatusEnum.StateLost
            ) {
              throw createStateLostError(nextStatus.error);
            }
            throw new ChatUatFailedError(
              nextStatus.error,
              nextStatus.jobId,
              nextStatus.failureCode
            );
          }
          if (
            nextStatus.status !==
            ChatUatJobCompletedResponseStatusEnum.Completed
          ) {
            throw new Error("Invalid ChatUAT job response");
          }
          return validateAcceptedPayload(() =>
            validateResponse(nextStatus.response, turnConversationId)
          );
        } catch (error) {
          if (
            isPollHttpError(error) ||
            shouldPreserveAcceptedJobAfterDeadline(error, deadline)
          ) {
            throw createOutcomeUncertainError();
          }
          throw error;
        }
      };

      try {
        if (resumeOnly) {
          const resumedResponse = await pollJob();
          return resumedResponse;
        }
        let startResult: { data: unknown };
        try {
          startResult = await api.startChatUatJob(
            {
              jobId,
              prompt,
              conversationId: turnConversationId,
              ...(context ? { context } : {}),
              ...(turnAudience ? { audience: turnAudience } : {}),
            },
            {
              cancelToken: cancelSource.token,
            }
          );
        } catch (error) {
          if (axios.isCancel(error) && Date.now() >= deadline) {
            throw createOutcomeUncertainError();
          }
          if (!isTransportOrServerError(error)) {
            throw error;
          }
          try {
            await waitWithinDeadline(
              nextPollDelay(MIN_POLL_DELAY_MS, 0),
              cancelSource,
              deadline
            );
          } catch (pollDelayError) {
            if (
              shouldPreserveAcceptedJobAfterDeadline(pollDelayError, deadline)
            ) {
              throw createOutcomeUncertainError();
            }
            throw pollDelayError;
          }
          return await pollJob();
        }
        const startStatus = validateAcceptedPayload(() =>
          validateJobStatus(
            startResult.data,
            jobId,
            turnConversationId,
            "start"
          )
        );
        if (isPendingJobStatus(startStatus)) {
          return await pollJob(startStatus);
        }
        if (isTerminalJobStatus(startStatus)) {
          if (
            startStatus.status ===
            ChatUatJobStateLostResponseStatusEnum.StateLost
          ) {
            throw createOutcomeUncertainError();
          }
          throw new ChatUatFailedError(
            startStatus.error,
            startStatus.jobId,
            startStatus.failureCode
          );
        }
        if (
          startStatus.status === ChatUatJobCompletedResponseStatusEnum.Completed
        ) {
          return validateAcceptedPayload(() =>
            validateResponse(startStatus.response, turnConversationId)
          );
        }
        throw new Error("Invalid ChatUAT start response");
      } finally {
        clearTimeout(deadlineTimer);
      }
    },
    onSuccess: (data, turn) => {
      if (turn.generation !== generation.current) {
        return;
      }
      clearStoredChatUatTurn(turn);
      setIsOutcomeUncertain(false);
      conversationId.current = data.conversationId;
      if (data.responseType === ChatUatReviewResponseResponseTypeEnum.Review) {
        preservedArtefactFlow.current = false;
        setRawArtefact(data.artefact?.trim() || null);
      } else if (
        data.responseType === ChatUatActionResponseResponseTypeEnum.Action
      ) {
        preservedArtefactFlow.current = false;
      }
      setEntries((prev) => [
        ...prev,
        {
          id: nextEntryId(),
          role: "assistant",
          content: data.conversational.trim(),
          groundedIn: data.groundedIn ? Array.from(data.groundedIn) : undefined,
          action: data.action,
          isPassingReview: isPassingReview(data),
          feedbackTarget: {
            jobId: turn.jobId,
            conversationId: data.conversationId,
            response: data,
            context: turn.context,
          },
        },
      ]);
    },
    onError: (error, turn) => {
      if (turn.generation !== generation.current) {
        return;
      }
      if (isOutcomeUncertainError(error)) {
        setIsOutcomeUncertain(true);
        setEntries((prev) => [
          ...prev,
          {
            id: nextEntryId(),
            role: "assistant",
            content: error.message,
            isError: true,
          },
        ]);
        return;
      }
      clearStoredChatUatTurn(turn);
      if (isStateLostError(error)) {
        setIsOutcomeUncertain(true);
        preservedArtefactFlow.current = false;
        setEntries((prev) => [
          ...prev,
          {
            id: nextEntryId(),
            role: "assistant",
            content: error.message,
            isError: true,
          },
        ]);
        return;
      }
      setIsOutcomeUncertain(false);
      if (
        error instanceof ChatUatFailedError &&
        error.failureCode === "review_too_large"
      ) {
        conversationId.current = undefined;
        preservedArtefactFlow.current = false;
        setRawArtefact(null);
        setEntries([failureEntry(error)]);
        return;
      }
      if (error instanceof ChatUatFailedError && turn.preserveArtefact) {
        preservedArtefactFlow.current = false;
        setEntries((prev) => [...prev, failureEntry(error)]);
        return;
      }
      if (isPreAcceptanceClientError(error) && turn.preserveArtefact) {
        preservedArtefactFlow.current = false;
        setEntries((prev) => [
          ...prev,
          {
            id: nextEntryId(),
            role: "assistant",
            content: REQUEST_REJECTED_ERROR,
            isError: true,
          },
        ]);
        return;
      }
      conversationId.current = undefined;
      preservedArtefactFlow.current = false;
      setRawArtefact(null);
      if (error instanceof ChatUatFailedError) {
        setEntries([failureEntry(error)]);
        return;
      }
      setEntries([
        {
          id: nextEntryId(),
          role: "assistant",
          content: REQUEST_RESET_ERROR,
          isError: true,
        },
      ]);
    },
    onSettled: (_data, error, turn) => {
      if (activeRequest.current === turn.cancelSource) {
        activeRequest.current = null;
      }
      if (turn.generation === generation.current) {
        submitting.current = false;
        if (turn.resumeOnly) {
          setIsResuming(false);
        }
      }
    },
  });
  const isLoading = mutationLoading || isResuming;

  useEffect(() => {
    if (!restoredTurn || resumeStarted.current) {
      return;
    }
    resumeStarted.current = true;
    const cancelSource = axios.CancelToken.source();
    activeRequest.current = cancelSource;
    mutate({
      jobId: restoredTurn.jobId,
      prompt: restoredTurn.prompt,
      conversationId: restoredTurn.conversationId,
      deadline: restoredTurn.deadline,
      generation: ++generation.current,
      cancelSource,
      resumeOnly: true,
      preserveArtefact: restoredTurn.artefact !== undefined,
      context: restoredTurn.context,
      audience: restoredTurn.audience,
    });
  }, [mutate, restoredTurn]);

  const sendMessage = useCallback(
    (text: string, options: SendMessageOptions = {}) => {
      const prompt = text.trim();
      if (!prompt || isLoading || submitting.current) {
        return;
      }
      const turnConversationId = conversationId.current ?? uuidv4();
      const deadline = Date.now() + JOB_TIMEOUT_MS;
      const preserveArtefact =
        options.preserveArtefact === true || preservedArtefactFlow.current;
      const turn = {
        jobId: uuidv4(),
        prompt,
        conversationId: turnConversationId,
        deadline,
        ...(currentContext ? { context: currentContext } : {}),
        ...(audience ? { audience } : {}),
      };
      const recoveryStateStored = storeChatUatTurn({
        ...turn,
        ...(preserveArtefact && rawArtefact ? { artefact: rawArtefact } : {}),
      });
      if (!recoveryStateStored) {
        setEntries((prev) => [
          ...prev,
          {
            id: nextEntryId(),
            role: "assistant",
            content: RECOVERY_STORAGE_ERROR,
            isError: true,
          },
        ]);
        return;
      }
      conversationId.current = turnConversationId;
      const cancelSource = axios.CancelToken.source();
      options.onSubmitted?.();
      setIsOutcomeUncertain(false);
      submitting.current = true;
      activeRequest.current = cancelSource;
      if (options.preserveArtefact === true) {
        preservedArtefactFlow.current = true;
      }
      if (!preserveArtefact) {
        setRawArtefact(null);
      }
      setEntries((prev) => [
        ...prev,
        { id: nextEntryId(), role: "user", content: prompt },
      ]);
      mutate({
        ...turn,
        generation: ++generation.current,
        cancelSource,
        resumeOnly: false,
        preserveArtefact,
      });
    },
    [audience, currentContext, isLoading, mutate, rawArtefact]
  );

  const submitFeedback = useCallback(
    async ({
      entryId,
      rating,
      reason,
      comment,
    }: ChatUatFeedbackInput): Promise<void> => {
      const entry = entries.find((candidate) => candidate.id === entryId);
      const target = entry?.feedbackTarget;
      if (!target) {
        throw new Error("This UAT Insights response cannot be rated.");
      }
      const fingerprint = await responseFingerprint(target.response);
      await ChatUatApiFactory().submitChatUatFeedback({
        feedbackId: uuidv4(),
        conversationId: target.conversationId,
        jobId: target.jobId,
        responseFingerprint: fingerprint,
        rating,
        ...(reason ? { reason } : {}),
        ...(comment?.trim() ? { comment: comment.trim() } : {}),
        ...(target.context
          ? {
              context: {
                runId: target.context.runId,
                taskGid: target.context.taskGid,
              },
            }
          : {}),
      });
      setEntries((previous) =>
        previous.map((candidate) =>
          candidate.id === entryId
            ? { ...candidate, feedbackRating: rating }
            : candidate
        )
      );
    },
    [entries]
  );

  const markFeedbackRating = useCallback(
    (entryId: string, rating: ChatUatFeedbackRequestRatingEnum) => {
      setEntries((previous) =>
        previous.map((candidate) =>
          candidate.id === entryId
            ? { ...candidate, feedbackRating: rating }
            : candidate
        )
      );
    },
    []
  );

  const reset = useCallback(() => {
    generation.current += 1;
    submitting.current = false;
    setIsResuming(false);
    setIsOutcomeUncertain(false);
    conversationId.current = undefined;
    preservedArtefactFlow.current = false;
    clearStoredChatUatTurn();
    activeRequest.current?.cancel("ChatUAT conversation reset");
    activeRequest.current = null;
    resetMutation();
    setEntries([]);
    setRawArtefact(null);
  }, [resetMutation]);

  const artefact = useMemo(
    () => (rawArtefact ? parseArtefact(rawArtefact) : null),
    [rawArtefact]
  );

  return {
    entries,
    artefact,
    sendMessage,
    submitFeedback,
    markFeedbackRating,
    reset,
    isLoading,
    isOutcomeUncertain,
  };
}
